import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Scatter } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Filler
);

interface ReportChartsProps {
  report: any;
}

export const ReportCharts: React.FC<ReportChartsProps> = ({ report }) => {
  if (!report) {
    return (
      <div className="text-center py-12 text-white/50">
        No report data available
      </div>
    );
  }

  const { results = {}, pitch_curves = {} } = report;
  const {
    distribution_score,
    pct_near_zero,
    distribution_std,
    anomaly_curve = [],
    time_points = [],
  } = results;

  const { reference = [], recording = [] } = pitch_curves;

  const rawScore = distribution_score ?? results.score ?? 0;
  const mainScore = useMemo(() => Math.round(rawScore * 10) / 10, [rawScore]);

  const concentrationPct = useMemo(
    () => (typeof pct_near_zero === 'number' ? Math.round(pct_near_zero * 100) : null),
    [pct_near_zero]
  );

  const dispersionVal = useMemo(
    () => (typeof distribution_std === 'number' ? distribution_std.toFixed(3) : null),
    [distribution_std]
  );

  const totalDuration = time_points?.length ? time_points[time_points.length - 1] : 0;

  const chartColors = {
    primary: '#f97316',
    secondary: '#3b82f6',
    success: '#22c55e',
    danger: '#ef4444',
    text: 'rgba(255, 255, 255, 0.7)',
    grid: 'rgba(255, 255, 255, 0.1)',
  };

  const { melodyData, melodyOptions } = useMemo(() => {
    if (!reference.length || !recording.length || !totalDuration) {
      return { melodyData: null, melodyOptions: null };
    }

    const refPoints = reference.map((v: number, i: number) => ({
      x: (i / reference.length) * totalDuration,
      y: v,
    }));

    const recPoints = recording.map((v: number, i: number) => ({
      x: (i / recording.length) * totalDuration,
      y: v,
    }));

    return {
      melodyData: {
        datasets: [
          {
            label: 'Reference',
            data: refPoints,
            borderColor: chartColors.secondary,
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            pointRadius: 0,
            borderWidth: 1.5,
            fill: true,
          },
          {
            label: 'Recording',
            data: recPoints,
            borderColor: chartColors.primary,
            backgroundColor: 'rgba(249, 115, 22, 0.1)',
            pointRadius: 0,
            borderWidth: 1.5,
            fill: true,
          },
        ],
      },
      melodyOptions: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index' as const, intersect: false },
        parsing: false,
        plugins: {
          legend: {
            display: true,
            labels: { color: chartColors.text },
          },
        },
        scales: {
          x: {
            type: 'linear' as const,
            title: { display: true, text: 'Time (s)', color: chartColors.text },
            ticks: { color: chartColors.text },
            grid: { color: chartColors.grid },
          },
          y: {
            title: { display: true, text: 'Pitch (Hz)', color: chartColors.text },
            ticks: { color: chartColors.text },
            grid: { color: chartColors.grid },
          },
        },
      },
    };
  }, [reference, recording, totalDuration]);

  const { timelineData, timelineOptions } = useMemo(() => {
    if (!anomaly_curve.length || !time_points.length) {
      return { timelineData: null, timelineOptions: null };
    }

    const points = anomaly_curve.map((v: number, i: number) => ({
      x: time_points[i],
      y: v,
    }));

    return {
      timelineData: {
        datasets: [
          {
            label: 'Deviation',
            data: points,
            borderColor: chartColors.primary,
            backgroundColor: 'rgba(249, 115, 22, 0.2)',
            pointRadius: 0,
            borderWidth: 1.5,
            fill: true,
          },
        ],
      },
      timelineOptions: {
        responsive: true,
        maintainAspectRatio: false,
        parsing: false,
        plugins: {
          legend: { display: false },
        },
        scales: {
          x: {
            type: 'linear' as const,
            title: { display: true, text: 'Time (s)', color: chartColors.text },
            ticks: { color: chartColors.text },
            grid: { color: chartColors.grid },
          },
          y: {
            title: { display: true, text: 'Deviation', color: chartColors.text },
            ticks: { color: chartColors.text },
            grid: { color: chartColors.grid },
          },
        },
      },
    };
  }, [anomaly_curve, time_points]);

  const { deviationMapData, deviationMapOptions } = useMemo(() => {
    if (!anomaly_curve.length) {
      return { deviationMapData: null, deviationMapOptions: null };
    }

    const nPoints = anomaly_curve.length;
    const angles = anomaly_curve.map((_: number, i: number) => (i / nPoints) * Math.PI * 2);
    const radii = anomaly_curve.map((v: number) => Math.abs(v));

    const sharp: { x: number; y: number }[] = [];
    const flat: { x: number; y: number }[] = [];

    for (let i = 0; i < nPoints; i++) {
      const point = {
        x: radii[i] * Math.cos(angles[i]),
        y: radii[i] * Math.sin(angles[i]),
      };
      if (anomaly_curve[i] >= 0) {
        sharp.push(point);
      } else {
        flat.push(point);
      }
    }

    return {
      deviationMapData: {
        datasets: [
          {
            label: 'Sharp (+)',
            data: sharp,
            pointRadius: 2,
            backgroundColor: chartColors.success,
          },
          {
            label: 'Flat (-)',
            data: flat,
            pointRadius: 2,
            backgroundColor: chartColors.danger,
          },
        ],
      },
      deviationMapOptions: {
        responsive: true,
        maintainAspectRatio: false,
        parsing: false,
        plugins: {
          legend: {
            display: true,
            labels: { color: chartColors.text },
          },
        },
        scales: {
          x: {
            type: 'linear' as const,
            min: -1.1,
            max: 1.1,
            ticks: { display: false },
            grid: { color: chartColors.grid, circular: true },
          },
          y: {
            type: 'linear' as const,
            min: -1.1,
            max: 1.1,
            ticks: { display: false },
            grid: { color: chartColors.grid },
          },
        },
      },
    };
  }, [anomaly_curve]);

  const { histData, histOptions } = useMemo(() => {
    if (!anomaly_curve.length) {
      return { histData: null, histOptions: null };
    }

    const bins = [-1.0, -0.75, -0.5, -0.25, 0, 0.25, 0.5, 0.75, 1.0];
    const histogram = new Array(bins.length - 1).fill(0);

    anomaly_curve.forEach((val: number) => {
      for (let i = 0; i < bins.length - 1; i++) {
        if (val >= bins[i] && val < bins[i + 1]) {
          histogram[i]++;
          break;
        }
      }
    });

    const labels = bins.slice(0, -1).map((b) => b.toFixed(2));

    return {
      histData: {
        labels,
        datasets: [
          {
            label: 'Frequency',
            data: histogram,
            backgroundColor: histogram.map((_, i) =>
              i >= 4 ? chartColors.success : chartColors.danger
            ),
            borderRadius: 4,
          },
        ],
      },
      histOptions: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y' as const,
        plugins: {
          legend: { display: false },
        },
        scales: {
          x: {
            title: { display: true, text: 'Count', color: chartColors.text },
            ticks: { color: chartColors.text },
            grid: { color: chartColors.grid },
          },
          y: {
            title: { display: true, text: 'Deviation Range', color: chartColors.text },
            ticks: { color: chartColors.text },
            grid: { color: chartColors.grid },
          },
        },
      },
    };
  }, [anomaly_curve]);

  const hasChartData = melodyData || timelineData || deviationMapData || histData;

  if (!hasChartData && mainScore === 0) {
    return (
      <div className="text-center py-12 text-white/50">
        No visualization data available in this report
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-kod-purple-900/50 rounded-xl p-6 text-center">
        <p className="text-white/60 text-sm mb-2">
          Recording: {report.recording_id || 'Unknown'}
        </p>

        <div className="inline-flex flex-col items-center justify-center px-12 py-6 rounded-full border-4 border-kod-accent bg-kod-accent/10">
          <div className="flex items-baseline">
            <span className="text-5xl font-bold text-kod-accent">
              {mainScore.toFixed(1)}
            </span>
            <span className="text-2xl text-kod-accent ml-1">%</span>
          </div>
          <span className="text-white/60 text-sm mt-1">Distribution Score</span>
        </div>

        <div className="flex justify-center gap-8 mt-4 text-sm">
          <div>
            <span className="text-white/50">Concentration:</span>{' '}
            <span className="text-white font-medium">
              {concentrationPct !== null ? `${concentrationPct}%` : 'N/A'}
            </span>
          </div>
          <div>
            <span className="text-white/50">Dispersion:</span>{' '}
            <span className="text-white font-medium">
              {dispersionVal ?? 'N/A'}
            </span>
          </div>
        </div>
      </div>

      {melodyData && (
        <div className="bg-kod-purple-900/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 text-center">
            Melody Comparison
          </h3>
          <div className="h-64">
            <Line data={melodyData} options={melodyOptions} />
          </div>
        </div>
      )}

      {timelineData && (
        <div className="bg-kod-purple-900/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 text-center">
            Deviation Timeline
          </h3>
          <div className="h-64">
            <Line data={timelineData} options={timelineOptions} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {deviationMapData && (
          <div className="bg-kod-purple-900/50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 text-center">
              Deviation Map
            </h3>
            <div className="h-64">
              <Scatter data={deviationMapData} options={deviationMapOptions} />
            </div>
          </div>
        )}

        {histData && (
          <div className="bg-kod-purple-900/50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 text-center">
              Distribution Histogram
            </h3>
            <div className="h-64">
              <Bar data={histData} options={histOptions} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
